/**
 * PATH: packages/ui/src/components/find/findHighlightPainter.ts
 * FILE_ID: UI-FIND-HIGHLIGHT-PAINTER-001
 * PURPOSE: One uniform painter interface over the two in-page find
 *          highlight strategies, and the runtime choice between them.
 * WHY:     useInPageHighlight should orchestrate (observe, count, cycle,
 *          scroll) without branching on browser capability. It asks for a
 *          painter and gets the mutation-free Range one wherever the CSS
 *          Custom Highlight API exists, the legacy <mark> one elsewhere.
 *          `mutatesDom` tells the hook whether it must mute its own
 *          MutationObserver around an apply().
 * FLOW:
 *   ┌──────────────────┐   ┌───────────────────┐   ┌────────────────┐
 *   │ createFindPainter│ → │ range | mark      │ → │ apply/setActive│
 *   │ (capability)     │   │ painter           │   │ /clear         │
 *   └──────────────────┘   └───────────────────┘   └────────────────┘
 * DEPENDENCIES:
 *   - ./findHighlightRanges: CSS Custom Highlight implementation.
 *   - ./findHighlightDom: <mark> implementation (fallback).
 * RELATED:
 *   - useInPageHighlight.ts: the only consumer.
 */
import {
  applyHighlights,
  clearHighlights,
  setActiveHighlight,
} from './findHighlightDom'
import {
  clearRangeHighlights,
  collectMatchRanges,
  isRangeHighlightSupported,
  paintRangeHighlights,
  scrollRangeIntoView,
} from './findHighlightRanges'

export interface FindHighlightPainter {
  /** Recompute the hits inside `container`. Returns the hit count. */
  apply: (container: HTMLElement, query: string) => number
  /** Paint hit `index` as the active one and scroll it into view. */
  setActive: (index: number) => void
  /** Remove every highlight this painter created. */
  clear: (container: HTMLElement) => void
  /**
   * Whether apply()/clear() write to the container's DOM. The hook mutes
   * its MutationObserver around those calls when true.
   */
  mutatesDom: boolean
}

/** Mutation-free painter: Ranges registered on CSS.highlights. */
export function createRangePainter(): FindHighlightPainter {
  let ranges: Range[] = []
  return {
    mutatesDom: false,
    apply(container, query) {
      ranges = collectMatchRanges(container, query)
      paintRangeHighlights(ranges, 0)
      return ranges.length
    },
    setActive(index) {
      paintRangeHighlights(ranges, index)
      scrollRangeIntoView(ranges[index])
    },
    clear() {
      ranges = []
      clearRangeHighlights()
    },
  }
}

/** Legacy painter: <mark> wrappers spliced into the container's text. */
export function createMarkPainter(): FindHighlightPainter {
  let marks: HTMLElement[] = []
  return {
    mutatesDom: true,
    apply(container, query) {
      marks = applyHighlights(container, query)
      return marks.length
    },
    setActive(index) {
      const active = setActiveHighlight(marks, index)
      active?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    },
    clear(container) {
      marks = []
      clearHighlights(container)
    },
  }
}

/** The best painter this browser supports. */
export function createFindPainter(): FindHighlightPainter {
  return isRangeHighlightSupported() ? createRangePainter() : createMarkPainter()
}
